import React, { useEffect, useState } from "react"

import { LeafletMouseEvent } from "leaflet"

import { Action } from "@tenforce/toolbox-button-push-react"
import { UrlBasedLayerOptions, GoogleLayerOptions } from "@tenforce/toolbox-map"
import { Modal } from "@tenforce/toolbox-modal-react"
import { DefaultLayers, Map as MapProductField } from "@tenforce/product-fields"

import { updateField } from "../../slice"
import { useAppDispatch, useAppSelector } from "../../../../hooks"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { GlobalState } from "../../../../store"
import {
    locationArrayToMap,
    convertLocationsToMarkers,
    getDefaultBounds,
} from "./helpers"
import { formatString } from "../../../../util/Strings"
import { Labels } from "../../../../util/Translations"
import { ILocationField, IMapField } from "../../index"

export interface Props {
    field: IMapField
}

export const MapField = ({ field }: Props): JSX.Element => {
    const {
        PlacePinForLocation = "Place pin for #{0}",
        ReplacePinForLocation = "Replace pin for #{0}",
    } = Labels
    const dispatch = useAppDispatch()

    const {
        position: _position,
        odataType: _odataType,
        value: locations,
        googleApiKey,
        description = "",
        ...fieldProps
    } = field

    const { isLoading, currentFieldInEditMode = "" } = useAppSelector(
        (state: GlobalState) => state.fields,
    )

    const [isModalShown, setIsModalShown] = useState(false)
    const [latitude, setLatitude] = useState(0)
    const [longitude, setLongitude] = useState(0)
    const [clientX, setClientX] = useState(0)
    const [clientY, setClientY] = useState(0)

    const [areMarkersBeingSaved, setAreMarkersBeingSaved] = useState(false)
    const [localLocations, setLocalLocations] = useState<
        Map<string, ILocationField>
    >(locationArrayToMap(locations))

    useEffect(() => {
        setLocalLocations(locationArrayToMap(locations))
        setAreMarkersBeingSaved(false)
    }, [locations])

    const isDisabled =
        fieldProps.isDisabled ||
        !["", fieldProps.fieldId].includes(currentFieldInEditMode)

    const markers = convertLocationsToMarkers(
        localLocations,
        isLoading || areMarkersBeingSaved,
        (newLocation) => {
            setLocalLocations(
                localLocations.set(newLocation.fieldId, newLocation),
            )
            setAreMarkersBeingSaved(true)
            dispatch(
                updateField({
                    field: newLocation,
                    newValue: newLocation.value,
                }),
            ).finally(() => {
                setAreMarkersBeingSaved(false)
            })
        },
        isDisabled,
    )

    let layers: (UrlBasedLayerOptions | GoogleLayerOptions)[] = []

    if (googleApiKey) {
        layers = layers.concat(
            DefaultLayers.GoogleMapLayer({ apiKey: googleApiKey }),
            DefaultLayers.GoogleTerrainLayer({ apiKey: googleApiKey }),
            DefaultLayers.GoogleSatelliteLayer({ apiKey: googleApiKey }),
            DefaultLayers.GoogleHybridLayer({ apiKey: googleApiKey }),
        )
    }
    layers = layers.concat(
        DefaultLayers.OpenStreetMapLayer({ isChecked: !googleApiKey }),
    )

    const locationsWithValue = locations.filter((location) => {
        return (
            location.value &&
            location.value.latitude &&
            location.value.longitude &&
            location.value.marker
        )
    })

    // if locations are different from each-other, let the map component do the bounds calculation
    // otherwise, determine default bounds based on the one location, or set it hardcoded
    const numberOfUniqueLocations = locationsWithValue
        .map(
            (location) =>
                `${location.value?.latitude},${location.value?.longitude}`,
        )
        .filter((value, index, array) => array.indexOf(value) === index).length

    const defaultBounds = getDefaultBounds(
        locationsWithValue,
        numberOfUniqueLocations === 1,
    )

    const editableLocations = Array.from(localLocations.values()).filter(
        (location) => {
            return !location.isReadOnly
        },
    )

    const contextMenuModal = (
        <Modal
            containerProps={{
                onClose: (): void => {
                    setIsModalShown(false)
                },
                style: {
                    position: "absolute",
                    left: `${scrollX + clientX}px`,
                    top: `${scrollY + clientY}px`,
                },
            }}
            isShown={isModalShown}
        >
            <div className="bg-default tw-ml-1 tw-p-2">
                <div className="tw-flex tw-items-center">
                    <div className="tw-w-full">
                        {editableLocations.map((location) => {
                            return (
                                <div className="tw-p-1" key={location.fieldId}>
                                    <div className="theme-thumb tw-w-full theme-thumb--no-margin bg-cloud">
                                        <div className="tw-w-full text-action">
                                            <Action
                                                onClick={async (): Promise<void> => {
                                                    const previousLocations =
                                                        localLocations
                                                    const newLocation = {
                                                        ...location,
                                                        value: {
                                                            ...location.value,
                                                            latitude: latitude,
                                                            longitude:
                                                                longitude,
                                                        },
                                                    } as ILocationField
                                                    setLocalLocations(
                                                        localLocations.set(
                                                            location.fieldId,
                                                            newLocation,
                                                        ),
                                                    )
                                                    setAreMarkersBeingSaved(
                                                        true,
                                                    )
                                                    await dispatch(
                                                        updateField({
                                                            field: newLocation,
                                                            newValue:
                                                                newLocation.value,
                                                        }),
                                                    )
                                                        .catch(() =>
                                                            setLocalLocations(
                                                                previousLocations,
                                                            ),
                                                        )
                                                        .finally(() => {
                                                            setAreMarkersBeingSaved(
                                                                false,
                                                            )
                                                            setIsModalShown(
                                                                false,
                                                            )
                                                        })
                                                }}
                                            >
                                                {location?.value?.latitude &&
                                                location?.value?.longitude
                                                    ? formatString(
                                                          ReplacePinForLocation,
                                                          location?.title,
                                                      )
                                                    : formatString(
                                                          PlacePinForLocation,
                                                          location?.title,
                                                      )}
                                            </Action>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </Modal>
    )

    return (
        <React.Fragment>
            {editableLocations.length > 0 && contextMenuModal}
            <MapProductField
                {...fieldProps}
                description={
                    typeof description === "string" ? (
                        <HTMLAndInternalLinkParser
                            content={description as string}
                        />
                    ) : (
                        field?.description
                    )
                }
                isClusteringEnabled={true}
                isLoading={areMarkersBeingSaved || isLoading}
                isDisabled={isDisabled}
                markers={markers}
                minZoom={1}
                bounds={numberOfUniqueLocations > 1 ? undefined : defaultBounds}
                layers={layers}
                eventHandlers={{
                    contextmenu: (leafletEvent: LeafletMouseEvent): void => {
                        if (editableLocations.length === 0) {
                            return
                        }
                        setLatitude(leafletEvent.latlng.lat)
                        setLongitude(leafletEvent.latlng.lng)
                        setClientX(leafletEvent.originalEvent.clientX)
                        setClientY(leafletEvent.originalEvent.clientY)
                        setIsModalShown(true)
                    },
                }}
            />
        </React.Fragment>
    )
}

export default MapField
